import { useEffect, useState } from 'react';
import { CalendarPlusIcon, ClockIcon, MapPinIcon, VideoIcon } from 'lucide-react';
import { toast } from 'sonner';
import { SlotPicker } from '../../../ui/SlotPicker';
import { Drawer } from '../../shared/Drawer';
import { founder, salon } from '../../../data/content';
import { useSite } from '../../../contexts/SiteContext';
import { shopLabels } from '../../../data/shopLabels';
import { canChange, dayLong, relative } from '../../../utils/account';
import { btnPrimary, btnQuiet, btnSecondary } from '../../../ui/styles';
import { FALLBACK_DURATION } from '../../../core/rules/agenda';
import { downloadCalendarFile } from '../../../utils/calendarFile';
import { staffList, withWhom } from '../../../core/rules/salonDays';
import { fetchAvailability, isAbortError } from '../../../utils/api';
import type { Appointment } from '../../../core/types/account';
import type { AvailabilityDay } from '../../../core/types/agenda';
type Props = {
  appointment: Appointment;
  name: string;
  minutes?: number;
  onMove: (date: string, time: string) => Promise<boolean>;
  onCancel: () => Promise<boolean>;
};

/** Eén komende afspraak. Verzetten en annuleren openen in een lade, zoals een nieuwe afspraak boeken. */
export function AppointmentCard({
  appointment: a,
  name,
  minutes,
  onMove,
  onCancel
}: Props) {
  const {
    policy,
    openingHours,
    staff
  } = useSite();
  // Gedeelde salon: "bij Mila". Alleen Sarah: niets extra.
  const who = withWhom(staffList(staff), a.staffId);
  const [mode, setMode] = useState<null | 'move' | 'cancel'>(null);
  const [busy, setBusy] = useState(false);
  const changeable = canChange(a);
  const when = `${dayLong(a.date)} om ${a.time}`;
  const panelId = `appointment-${a.id}-panel`;

  // Verzetten toont alleen écht vrije tijden (haar eigen afspraak telt niet mee). Lukt ophalen
  // niet, dan de openingstijden; de server weigert een bezette tijd alsnog met een melding.
  const [available, setAvailable] = useState<AvailabilityDay[] | null | undefined>(undefined);
  const [availabilityKey, setAvailabilityKey] = useState(0);
  useEffect(() => {
    if (mode !== 'move') return;
    const controller = new AbortController();
    setAvailable(null);
    fetchAvailability(a.treatmentSlug, {
      ignore: a.id
    }, {
      signal: controller.signal
    }).then(setAvailable).catch(err => {
      if (!isAbortError(err)) setAvailable(undefined);
    });
    return () => controller.abort();
  }, [mode, a.treatmentSlug, a.id, availabilityKey]);
  const move = async (date: string, time: string) => {
    setBusy(true);
    const ok = await onMove(date, time);
    setBusy(false);
    // Mislukt (bijv. net bezet): de kiezer blijft open met bijgewerkte tijden.
    if (!ok) {
      setAvailabilityKey(k => k + 1);
      return;
    }
    setMode(null);
    toast.success('Verzoek verstuurd', {
      description: `${founder.firstName} bevestigt je nieuwe tijd binnen ${policy.responseHours} uur.`
    });
  };
  const addToCalendar = () => {
    const ok = downloadCalendarFile({
      id: a.id,
      date: a.date,
      time: a.time,
      durationMinutes: minutes ?? FALLBACK_DURATION,
      title: who ? `${name} bij ${who}, ${salon.name}` : `${name} bij ${salon.name}`,
      location: a.kind === 'video' ? 'Videocall (link volgt per mail)' : salon.address,
      description: `${who ? `Je behandeling doet ${who}. ` : ''}Verzetten kan tot ${policy.cancelHours} uur van tevoren via Mijn omgeving.`
    });
    if (!ok) toast.error('Toevoegen lukte niet', {
      description: 'Je vindt de uitnodiging ook in je bevestigingsmail.'
    });
  };
  const cancel = async () => {
    setBusy(true);
    const ok = await onCancel();
    setBusy(false);
    if (!ok) return;
    setMode(null);
    toast('Afspraak geannuleerd', {
      description: 'Je krijgt een bevestiging per mail.'
    });
  };

  /** Sluiten mag, behalve terwijl een verzoek loopt: dan weet ze niet of het gelukt is. */
  const close = () => {
    if (!busy) setMode(null);
  };
  return <article className="bg-shell ring-1 ring-line" aria-labelledby={`appointment-${a.id}`} data-id="element-5882" data-mp="3a5s74l91eylo">
      <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-7" data-id="element-5883" data-mp="11xvimfjv4gx7">
        <div className="min-w-0" data-id="element-5884" data-mp="16qybelgtfmcc">
          <p className="flex flex-wrap items-center gap-2 text-[13px]" data-id="element-5885" data-mp="16r6s6og27bst">
            <span className="text-clay first-letter:uppercase" data-id="element-5886" data-mp="s2ajqqa3bwro">{relative(a.date)}</span>
            {a.status === 'aanvraag' && <span className="rounded-full bg-blush/70 px-2 py-0.5 text-[12px] text-ink" data-id="element-5887" data-mp="wxhev9pant4z">Wacht op bevestiging</span>}
          </p>
          <h3 id={`appointment-${a.id}`} className="mt-1 font-display text-[26px] leading-tight text-ink sm:text-[30px]" data-id="element-5888" data-mp="1pnzlult52eno">
            <span className="capitalize" data-id="element-5889" data-mp="1bnezxgrxg2py">{dayLong(a.date)}</span>, {a.time}
          </h3>
          <p className="mt-1.5 text-[15px] text-ink" data-id="element-5890" data-mp="1fpac5gbec0ii">
            {name}
            {a.session ? ` · sessie ${a.session}` : ''}
            {who ? ` · bij ${who}` : ''}
          </p>
          <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted" data-id="element-5891" data-mp="4an2l02ms02l">
            {minutes ? <span className="flex items-center gap-1.5" data-id="element-5892" data-mp="3svqgtxvrrjl3">
                <ClockIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-5893" data-mp="3nxovt7es9a6z" /> {minutes} minuten
              </span> : null}
            <span className="flex items-center gap-1.5" data-id="element-5894" data-mp="6o8ozhz0jno4">
              {a.kind === 'video' ? <VideoIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-5895" data-mp="1rs0gmnwj7ai" /> : <MapPinIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-5896" data-mp="3a6s4rl0i9121" />}
              {a.kind === 'video' ? 'Videocall' : salon.address}
            </span>
          </p>
        </div>

        {/* Blijft staan als de lade open is: daar gaat de focus na sluiten naar terug. */}
        <div className="flex shrink-0 flex-wrap gap-2" data-id="element-5897" data-mp="3f4235q5vgn20">
            {a.status === 'bevestigd' && <button type="button" onClick={addToCalendar} aria-label={`${name} op ${when} in je agenda zetten`} className={btnQuiet} data-id="element-5898" data-mp="2rdxxzed1cf8w">
                <CalendarPlusIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5899" data-mp="3c537u228kuw3" />
                In agenda
              </button>}
            {changeable ? <>
                <button type="button" onClick={() => setMode('move')} aria-label={`${name} op ${when} verzetten`} className={btnSecondary} data-id="element-5900" data-mp="3kwn2od3r6kls">
                  Verzetten
                </button>
                <button type="button" onClick={() => setMode('cancel')} aria-label={`${name} op ${when} annuleren`} className={btnQuiet} data-id="element-5901" data-mp="2t68w71y2kak5">
                  Annuleren
                </button>
              </> : <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnSecondary} data-id="element-5902" data-mp="325m61vq3v15e">
                App {founder.firstName}
                <span className="sr-only" data-id="element-5903" data-mp="373nocefpiwft">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>
      </div>

      {!changeable && <p className="border-t border-line px-5 py-3 text-[13px] text-muted sm:px-7" data-id="element-5904" data-mp="meq69fkc7eez">
          Binnen {policy.cancelHours} uur verzetten gaat via {founder.firstName} persoonlijk. Stuur even een appje.
        </p>}

      {/*
        Verzetten en annuleren openen in dezelfde lade als een nieuwe afspraak: op de telefoon één
        vlak met de tijdkiezer, niet een kaart die de pagina af groeit. Tijdens een verzoek sluit hij niet.
       */}
      <Drawer open={mode !== null} onClose={close} title={mode === 'cancel' ? 'Afspraak annuleren' : 'Afspraak verzetten'} data-id="element-5905" data-mp="1s6l2noxrpoge">
        <div className="mb-6 bg-sand p-4" data-id="element-5906" data-mp="1j894n1uknodx">
          <p className="text-[13px] text-muted" data-id="element-5907" data-mp="1j76g30kq1y16">Je afspraak nu</p>
          <p className="mt-0.5 text-[15px] text-ink first-letter:uppercase" data-id="element-5908" data-mp="1e8d0n2lvjwyd">{when}</p>
          <p className="text-[14px] text-muted" data-id="element-5909" data-mp="19a1oumc0rcdi">
            {name}
            {a.session ? ` · sessie ${a.session}` : ''}
          </p>
        </div>
        {mode === 'move' ? <SlotPicker hours={openingHours} available={available} durationMinutes={minutes ?? FALLBACK_DURATION} busy={busy} onCancel={close} onConfirm={(date, time) => void move(date, time)} data-id="element-5910" data-mp="38kjl6s0nrq3c" /> : <div role="group" aria-labelledby={`${panelId}-title`} aria-describedby={`${panelId}-body`} data-id="element-5911" data-mp="2hhjz770u57lz">
            <p id={`${panelId}-title`} className="text-[15px] text-ink" data-id="element-5912" data-mp="23q53q0pqrdy1">
              Deze afspraak annuleren?
            </p>
            <p id={`${panelId}-body`} className="mt-1 text-[14px] text-muted" data-id="element-5913" data-mp="27qyr8sz9uqqe">
              Liever een andere dag? Verzetten kan ook. Je krijgt een bevestiging per mail.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-id="element-5914" data-mp="1yrli61f28467">
              <button type="button" disabled={busy} onClick={() => setMode('move')} className={btnPrimary} data-id="element-5915" data-mp="1yoz91xcitglv">
                Liever verzetten
              </button>
              <button type="button" disabled={busy} aria-busy={busy} onClick={() => void cancel()} className={btnSecondary} data-id="element-5916" data-mp="1otxjx1i8weja">
                {busy ? 'Annuleren…' : 'Ja, annuleren'}
              </button>
            </div>
          </div>}
      </Drawer>
    </article>;
}